import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';

import { Button, CircularProgress, IconButton, MenuItem, MenuList, withStyles, WithStyles } from '@material-ui/core';
import { InsertDriveFile as LaunchFormIcon } from '@material-ui/icons';
import { filter, isEmpty, map } from 'lodash';
import queryString from 'query-string';

import { patientFormTemplatesResource, widgetUsersResource } from '@lib/resources';
import { PatientFormTemplate } from '@lib/resources/types';

import MenuContainer from 'components/MenuContainer';
import Tooltip from 'components/Tooltip';
import Typography from 'components/v2/Typography';
import { useSitePatient } from 'hooks';
import usePermissions from 'hooks/usePermissions';

import { useStyles } from './LaunchForms.useStyles';

interface LaunchFormsProps extends WithStyles<typeof useStyles> {
  iconColor?: string;
}

interface FormTemplate extends PatientFormTemplate {
  _id: string;
}

const LaunchForms: React.FC<LaunchFormsProps> = ({ classes, iconColor }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [widgetUser, setWidgetUser] = useState<any>();
  const [loadingFormList, setLoadingFormList] = useState(true);
  const [formsList, setFormsList] = useState<FormTemplate[]>([]);
  const { permissions } = usePermissions();
  const dispatch = useDispatch();
  const displayFormBuilderButton = !!permissions && !permissions.isSubaccount;
  const sitePatient = useSitePatient();

  useEffect(() => {
    if (!widgetUser) {
      dispatch(
        widgetUsersResource.request({
          method: 'get',
          url: '/widgets/user',
          meta: {
            callbackSuccess: ({ data }) => {
              setWidgetUser(data.id);
            },
          },
        }),
      );
    }
  }, [dispatch, widgetUser]);

  const handleClickLaunchForms = (event: any) => {
    if (!event || menuAnchor) {
      setMenuAnchor(null);
    } else if (!menuAnchor) {
      dispatch(
        patientFormTemplatesResource.request({
          method: 'get',
          url: '/patientFormTemplates',
          params: {
            // The limit here was changed to tackle fast a problem that a client is facing. A better solution for this will be discussed and applied by https://lumahealth.clickup.com/t/3z8x68
            limit: 500,
            lumabot: false,
          },
          meta: {
            callbackSuccess: ({ data: { response } }: any) => {
              // FIXME: Pass the `type` parameter to the call instead of filtering the result
              const forms = filter(response, (form) => form.type === 'pre-qualification');
              setFormsList(forms);
              setLoadingFormList(false);
            },
          },
        }),
      );
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleFormClick = (form: any) => {
    const params = {
      patientFormTemplate: form._id,
      user: widgetUser || '',
    };

    let url = `${sitePatient}/survey?${queryString.stringify(params)}`;
    if (!/luma/i.test(form.externalId.source)) {
      url = form.template.preview;
    }
    window.open(url, '_blank');
    setMenuAnchor(null);
  };

  const handleGoToFormBuilder = () => {
    browserHistory.push('/settings/features/forms');
    setMenuAnchor(null);
  };

  return (
    <>
      <Tooltip className={classes.tooltip} hideArrow title="Launch Forms" placement="bottom">
        <IconButton
          aria-label="Launch Forms"
          className={classes.iconButton}
          style={iconColor ? { color: iconColor } : {}}
          color="primary"
          onClick={handleClickLaunchForms}
        >
          <LaunchFormIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <MenuContainer
        classNameFade={classes.fade}
        menuAnchor={menuAnchor}
        onClickAway={() => setMenuAnchor(null)}
        placement="bottom"
      >
        {loadingFormList && (
          <div className={classes.progressWrapper}>
            <CircularProgress size={24} />
          </div>
        )}
        {isEmpty(formsList) && !loadingFormList ? (
          <div className={classes.wrapper}>
            <Typography color="primary" variant="body1" gutterBottom>
              No forms created yet.
            </Typography>
            {displayFormBuilderButton && (
              <Button
                className={classes.goToFormBuilder}
                variant="contained"
                color="primary"
                onClick={handleGoToFormBuilder}
              >
                Go to Form Builder
              </Button>
            )}
          </div>
        ) : (
          <MenuList>
            {map(formsList, (form: FormTemplate) => (
              <MenuItem onClick={() => handleFormClick(form)} key={form._id}>
                <Typography color="primary" truncate variant="body1">
                  {form.title}
                </Typography>
              </MenuItem>
            ))}
          </MenuList>
        )}
      </MenuContainer>
    </>
  );
};

export default withStyles(useStyles)(LaunchForms);
